'use client';

import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { usePortfolioData } from '@/context/PortfolioDataContext';

export default function ExperienceSection() {
  const { t } = useLanguage();
  const { experience } = usePortfolioData();

  const displayExperiences = (experience ?? []).map((exp) => ({
    period: exp.period,
    role: exp.role,
    company: exp.company,
    type: `${exp.work_type} · ${exp.location}`,
    description: exp.description,
    highlights: exp.highlights || [],
    current: exp.is_current,
  }));

  return (
    <section id="experience" className="relative py-28 px-6 overflow-hidden">
      <div
        className="absolute inset-0 z-0"
        style={{ background: 'linear-gradient(180deg, #1B2432 0%, #141c28 100%)' }}
      />
      <div
        className="absolute left-0 top-1/2 -translate-y-1/2 pointer-events-none z-0"
        style={{
          width: 400,
          height: 500,
          background: 'radial-gradient(ellipse at left, rgba(99,179,237,0.06) 0%, transparent 70%)',
        }}
      />
      <div className="relative z-10 max-w-3xl mx-auto">
        <div className="reveal flex items-center gap-3 mb-4">
          <div
            className="h-px flex-1 max-w-[48px]"
            style={{ background: 'rgba(99,179,237,0.5)' }}
          />
          <span
            className="text-xs font-semibold uppercase tracking-[0.22em]"
            style={{ color: '#63b3ed' }}
          >
            {t?.exp_label}
          </span>
        </div>

        <h2
          className="reveal font-display font-bold mb-16 reveal-delay-1"
          style={{
            fontSize: 'clamp(2rem, 5vw, 3.25rem)',
            color: '#EDF2F7',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
          }}
        >
          {t?.exp_heading_1}{' '}
          <span
            style={{
              background: 'linear-gradient(135deg, #63b3ed, #a78bfa)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t?.exp_heading_2}
          </span>
        </h2>

        <div className="space-y-0">
          {displayExperiences?.map((exp, i) => (
            <div
              key={i}
              className={`reveal reveal-delay-${i + 1} relative flex gap-8 pb-12 last:pb-0`}
            >
              {i < displayExperiences?.length - 1 && (
                <div
                  className="absolute left-[19px] top-[44px] bottom-0 w-px"
                  style={{
                    background:
                      'linear-gradient(to bottom, rgba(99,179,237,0.4), rgba(99,179,237,0.04))',
                  }}
                />
              )}
              <div className="relative z-10 flex-shrink-0 mt-1">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{
                    background: exp?.current ? 'rgba(99,179,237,0.15)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${exp?.current ? 'rgba(99,179,237,0.5)' : 'rgba(200,206,214,0.12)'}`,
                  }}
                >
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{
                      background: exp?.current ? '#63b3ed' : 'rgba(200,206,214,0.3)',
                      boxShadow: exp?.current ? '0 0 8px rgba(99,179,237,0.6)' : 'none',
                    }}
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2.5 flex-wrap mb-1">
                      <h3 className="font-display font-bold text-lg" style={{ color: '#EDF2F7' }}>
                        {exp?.role}
                      </h3>
                      {exp?.current && (
                        <span
                          className="text-xs font-bold px-2 py-0.5 rounded-full"
                          style={{
                            background: 'rgba(34,197,94,0.1)',
                            border: '1px solid rgba(34,197,94,0.3)',
                            color: '#22c55e',
                          }}
                        >
                          {t?.exp_current_badge}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium" style={{ color: 'rgba(237,242,247,0.5)' }}>
                      {exp?.company}
                      <span style={{ color: 'rgba(237,242,247,0.25)' }}> · {exp?.type}</span>
                    </p>
                  </div>
                  <span
                    className="text-xs font-mono font-medium px-3 py-1.5 rounded-lg flex-shrink-0"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(200,206,214,0.1)',
                      color: 'rgba(237,242,247,0.4)',
                    }}
                  >
                    {exp?.period}
                  </span>
                </div>
                <p
                  className="text-sm leading-relaxed mb-4"
                  style={{ color: 'rgba(237,242,247,0.55)' }}
                >
                  {exp?.description}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {exp?.highlights?.map((h: string) => (
                    <span
                      key={h}
                      className="text-xs px-2.5 py-1 rounded-lg font-medium"
                      style={{
                        background: 'rgba(99,179,237,0.08)',
                        border: '1px solid rgba(99,179,237,0.2)',
                        color: 'rgba(99,179,237,0.8)',
                      }}
                    >
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(200,206,214,0.12), transparent)',
        }}
      />
    </section>
  );
}
